import React, { useEffect, useRef, useState } from "react";
import LoaderComp from '../../components/Loader/LoaderComp';
import { WorkSpaceItemRecursive } from "../../model/ItemModel";
import { GetTreeByWorkspaceFolderIdAll } from "../../services/Item/ItemServicesGet";
import { GetOneWorkSpace } from "../../services/WorkSpaceServices";
import TreeComponent from "./TreeComponent";
import WorkSpaceItemListFolderComponent from "./WorkSpaceItemListFolderComponent";

import { INavLink } from "@fluentui/react";
import { FolderStructureIcon } from "../../assets/icons/folderStructureIcon";
import { Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router";

interface WorkSpaceItemListFolderTreeProps {
    setPopupOkMessage: (ok: boolean, message: string) => void;
    setPopupErroMessage: (ok: boolean, message: string) => void;
    clickFunction: (idFolder: string | undefined, nameFolder: string | undefined) => void;
    clickFunctionWorkspace: (nameWorkspace: string | undefined) => void;
    onDrop?: (event: React.DragEvent<HTMLDivElement>, key: string | undefined, endFunction: () => void) => void;
    reloadFolders: () => void;
    targetId?: string;
}

const WorkSpaceItemListFolderTree: React.FC<WorkSpaceItemListFolderTreeProps> = ({
    setPopupOkMessage,
    setPopupErroMessage,
    clickFunction,
    clickFunctionWorkspace,
    onDrop,
    targetId
}) => {
    const [reload, setReload] = useState<boolean>(false);
    const [displayNav, setDisplayNav] = useState<boolean>(false);
    const [nav, setNav] = useState<WorkSpaceItemRecursive[]>([]);
    const [nameWork, setNameWork] = useState<string>();
    const [isHover, setIsHover] = useState<boolean>(false);
    const [expandedLinks, setExpandedLinks] = useState<{ [key: string]: boolean }>({});
    const [selectedFolderId, setSelectedFolderId] = useState<string | undefined>(undefined);
    const [widthWork, setWidthWork] = useState<number>(0);
    const { t } = useTranslation();
    const [isCollapsed, setIsCollapsed] = useState<boolean>(false);
    const { idWorkspace, idFolder } = useParams();
    const handleIconClick = (event: React.MouseEvent) => {
        event.stopPropagation();
        setIsCollapsed(!isCollapsed);
    };

    useEffect(() => {
        if (idFolder) {
            expandToDestinationAndSelect();
        } else {
            setSelectedFolderId(idWorkspace)
        }
    }, [idFolder, idWorkspace, nav]);

    useEffect(() => {
        const loadWorkspaceData = async () => {
            setReload(true);

            try {
                const workspaceItems = await GetTreeByWorkspaceFolderIdAll(idWorkspace!);
                setNav(workspaceItems);

                const workspace = await GetOneWorkSpace(idWorkspace!);
                if (workspace) {
                    setNameWork(workspace.name);
                }

                setDisplayNav(true);
            } catch (error) {
                console.error(error);
            } finally {
                setReload(false);
            }
        };

        loadWorkspaceData();
    }, [idWorkspace]);

    const toggleExpand = (key: string) => {
        setExpandedLinks(prev => ({
            ...prev,
            [key]: !prev[key],
        }));
    };

    const preventDefaultBehaviour = (event: React.DragEvent<HTMLDivElement>) => {
        event.stopPropagation();

        event.preventDefault();
    };

    const onDragEnterAndOver = (event: React.DragEvent<HTMLDivElement>) => {
        preventDefaultBehaviour(event);
        setIsHover(true);
    };

    const onDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        preventDefaultBehaviour(event);
        setIsHover(false);
    };

    const leave = () => {
        setIsHover(false);
    };

    const parentRef = useRef<HTMLDivElement>(null);

    function handleWidth(width: number) {
        setWidthWork(width)
    }

    function findExpandedPath(
        items: WorkSpaceItemRecursive[],
        targetId: string | undefined,
        path: string[] = []
    ): string[] | null {
        for (const item of items) {
            const currentPath: string[] = [...path, item.id!];
            if (item.id === targetId) {
                return currentPath;
            }
            if (item.children && item.children.length > 0) {
                const result = findExpandedPath(item.children, targetId, currentPath);
                if (result) {
                    return result;
                }
            }
        }
        return null;
    }

    function expandToDestinationAndSelect() {
        setSelectedFolderId(idFolder);
            if (idFolder && nav.length > 0) {
                const expandedPath = findExpandedPath(nav, idFolder);
                if (expandedPath) {
                    const expandedObj = expandedPath.slice(0, -1).reduce((acc, id) => {
                        acc[id] = true;
                        return acc;
                    }, {} as { [key: string]: boolean });
                    setExpandedLinks(expandedObj);
                }
            }
    }
    
    return (
        nav.length > 0 ? (
            reload ? (
            <div><LoaderComp big={true} /></div>
        ) : (
            <div ref={parentRef} style={{
                overflowX: isCollapsed ? "hidden" : "auto",
                border: "1px solid #E0E0E0",
                borderRadius: "4px",
                width: isCollapsed ? "36px" : "236px",
                transition: "width 0.3s ease",
                height: "calc(100vh - 325px)",
                marginRight: "12px",
            }}>

                <div
                    className={isHover ? "hoverDrop name" : "name"}
                    onClick={() => { clickFunctionWorkspace(nameWork); setSelectedFolderId(idWorkspace) }}
                    onDragEnter={onDragEnterAndOver}
                    onDragLeave={onDragLeave}
                    onDragOver={onDragEnterAndOver}
                    onDrop={onDrop == undefined ? undefined : (e) => onDrop(e, undefined, leave)}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        padding: "8px",
                        cursor: "pointer",
                        font: selectedFolderId === idWorkspace ? 'normal semibold 600 14px/21px Segoe UI' : 'normal normal 400 14px/21px Segoe UI',
                        width: isCollapsed ? "36px" : "194px",
                        overflow: "hidden",
                    }}
                >
                    <div onClick={handleIconClick} style={{ display: "flex", alignItems: "center", flexShrink: 0 }}>
                        <FolderStructureIcon color={"#22638D"} />
                    </div>
                    {!isCollapsed && (
                        <Typography textAlign={'center'} sx={{ font: 'normal normal 400 12px/18px Segoe UI', marginLeft: '4px', whiteSpace: 'nowrap' }}>
                            {t("FolderStructure.title")}
                        </Typography>
                    )}
                </div>

                {(displayNav && !isCollapsed) &&
                    <TreeComponent
                        idWorkspace={idWorkspace!}
                        setPopupError={setPopupErroMessage}
                        setPopupSuccess={setPopupOkMessage}
                        onDrop={onDrop}
                        clickFunction={clickFunction}
                        setSelectedFolderId={setSelectedFolderId}
                        expandedLinks={expandedLinks}
                        items={nav}
                        selectedFolderId={selectedFolderId}
                        toggleExpand={toggleExpand}
                        treeItemComponent={WorkSpaceItemListFolderComponent}
                        setWidth={handleWidth}
                    />
                }
            </div>
        )
        ) : (
            <div></div>)
    );
};

export default WorkSpaceItemListFolderTree;
